import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { billingApi } from '@/lib/billing';
import { companiesApi, Company } from '@/lib/companies';
import { membersApi, memberLabel, WorkspaceMember, AssignableRole } from '@/lib/members';
import { api } from '@/lib/api';
import { AppShell } from '@/components/app/AppShell';
import { CountryPicker } from '@/components/CountryPicker';
import { CurrencyPicker } from '@/components/CurrencyPicker';
import { ConfirmModal } from '@/components/ConfirmModal';
import { McpSection } from '@/components/app/McpSection';
import { LANGUAGES } from '@/i18n/languages';
import { useCurrentUser } from '@/hooks/useAuth';

interface WorkspaceLite { id: string; name: string; fyLabel: string; companyId: string }
interface AiModelOption { id: string; label: string }

export function SettingsPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  // Inline error string for the upgrade button — replaces the previous
  // `alert(err.message)`. Cleared on next attempt; sits beside the CTA.
  const [billingBanner, setBillingBanner] = useState<'success' | 'cancelled' | null>(null);
  const qc = useQueryClient();

  // Stripe return handling. When checkout completes, Stripe sends the
  // user back to `?billing=success`; if they bail it's `?billing=cancelled`.
  // We surface a banner, refresh the queries that derive plan + portal
  // availability, and strip the param so a refresh doesn't re-trigger it.
  useEffect(() => {
    const status = searchParams.get('billing');
    if (status === 'success' || status === 'cancelled') {
      setBillingBanner(status);
      if (status === 'success') {
        qc.invalidateQueries({ queryKey: ['companies'] });
        qc.invalidateQueries({ queryKey: ['billing-me'] });
      }
      const next = new URLSearchParams(searchParams);
      next.delete('billing');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { data: user } = useCurrentUser();
  const isSiteAdmin = user?.isAdmin === true;
  const { data: workspace } = useQuery({
    queryKey: ['workspace', wsId],
    queryFn: async () => (await api.get<WorkspaceLite>(`/api/workspaces/${wsId}`)).data,
    enabled: !!wsId,
  });

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesApi.list(),
    enabled: true,
  });
  const company = companies.find((c) => c.id === workspace?.companyId);

  const { data: usage } = useQuery({
    queryKey: ['ai-usage', wsId],
    queryFn: () => billingApi.aiUsage(wsId!),
    enabled: !!wsId,
  });

  // Model catalog is fetched only for site admins; non-admins never see
  // the underlying model identifiers (neither in the bundle nor in any
  // network response). The /api/admin/* surface is RolesGuard-protected.
  const { data: modelCatalog } = useQuery({
    queryKey: ['admin', 'ai-models'],
    queryFn: async (): Promise<{ models: AiModelOption[] }> => (await api.get('/api/admin/ai-models')).data,
    enabled: isSiteAdmin,
    staleTime: 5 * 60 * 1000,
  });
  const modelOptions: AiModelOption[] = modelCatalog?.models ?? [];

  const [capUsd, setCapUsd] = useState<string>('');
  const [modelDefault, setModelDefault] = useState<string>('');
  const [reasoningModel, setReasoningModel] = useState<string>('');

  // Seed the model selectors from the company once it loads. Keyed on the
  // company values (not done during render — that is a React anti-pattern that
  // can loop). Only seeds while the local field is still empty, so it never
  // clobbers an in-progress edit.
  useEffect(() => {
    if (!company) return;
    setModelDefault((prev) => (prev === '' ? company.aiModelDefault : prev));
    setReasoningModel((prev) => (prev === '' ? company.aiReasoningModel : prev));
  }, [company]);

  const update = useMutation({
    mutationFn: (patch: { aiMonthlyCapCents?: number | null; aiModelDefault?: string; aiReasoningModel?: string }) =>
      companiesApi.updateSettings(company!.id, patch),
    onSuccess: (next: Company) => {
      qc.setQueryData<Company[]>(['companies'], (prev) => prev?.map((c) => (c.id === next.id ? next : c)) ?? [next]);
      qc.invalidateQueries({ queryKey: ['ai-usage', wsId] });
      setCapUsd('');
    },
  });
function saveCap() {
    if (!company) return;
    if (capUsd === '') {
      update.mutate({ aiMonthlyCapCents: null });
    } else {
      const cents = Math.round(Number(capUsd) * 100);
      if (Number.isFinite(cents) && cents > 0) update.mutate({ aiMonthlyCapCents: cents });
    }
  }
  function saveModels() {
    if (!company) return;
    update.mutate({ aiModelDefault: modelDefault, aiReasoningModel: reasoningModel });
  }

  return (
    <AppShell title={t('settings.title')} subtitle={undefined}>
<main className="mx-auto max-w-4xl px-6 py-10 space-y-8">
        <div>
          <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">{t('settings.title')}</h1>
          <p className="mt-1 text-ink-600">{workspace?.name}</p>
        </div>

        {company && <CompanyProfileSection company={company} t={t} />}
        {wsId && <MembersSection wsId={wsId} t={t} />}
        {wsId && <McpSection wsId={wsId} />}

        <section className="rounded-2xl border border-ink-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-ink-900">{t('settings.ai_section')}</h2>
          <p className="text-sm text-ink-600 mt-1">{t('settings.ai_intro')}</p>
          <dl className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <div>
              <dt className="text-ink-500">{t('settings.spent_mtd')}</dt>
              <dd className="num text-2xl font-semibold text-ink-900">${((usage?.monthCents ?? 0) / 100).toFixed(2)}</dd>
            </div>
            <div>
              <dt className="text-ink-500">{t('settings.cap')}</dt>
              <dd className="num text-2xl font-semibold text-ink-900">
                {usage?.capCents == null ? '—' : `$${(usage.capCents / 100).toFixed(0)}`}
              </dd>
              <p className="text-xs text-ink-500 mt-0.5">{t('settings.cap_help')}</p>
            </div>
            <div className="flex items-end gap-2">
              <input
                value={capUsd}
                onChange={(e) => setCapUsd(e.target.value)}
                placeholder={t('settings.cap_placeholder')}
                inputMode="decimal"
                className="num w-32 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
              />
              <button
                onClick={saveCap}
                disabled={update.isPending || !company}
                className="px-3 py-2 rounded-lg bg-accent-500 text-white text-xs font-semibold hover:bg-accent-600 disabled:opacity-60"
              >
                {update.isPending ? t('settings.saving') : t('settings.edit_cap')}
              </button>
            </div>
          </dl>

          {/*
           * Per-model spend breakdown is admin-only. Regular workspace
           * members see total AI spend above; the underlying model
           * names are an admin-only signal.
           */}
          {isSiteAdmin && usage && usage.byModel.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <h3 className="text-sm font-semibold text-ink-700 mb-2">{t('settings.spend_by_model')}</h3>
              <table className="w-full text-sm min-w-[480px]">
                <thead>
                  <tr className="text-left text-ink-500 border-b border-ink-100">
                    <th className="py-2 pr-3 font-medium">{t('common.model')}</th>
                    <th className="py-2 px-3 font-medium text-right">{t('settings.th_runs')}</th>
                    <th className="py-2 px-3 font-medium text-right">{t('settings.th_cents')}</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.byModel.map((m) => (
                    <tr key={m.model} className="border-b border-ink-50">
                      <td className="py-2 pr-3 num text-ink-900">{m.model}</td>
                      <td className="py-2 px-3 num text-right">{m.runs}</td>
                      <td className="py-2 px-3 num text-right">${(m.cents / 100).toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/*
         * Model configuration is a site-admin tool. Non-admins see a
         * read-only "CadencEZ AI" line — they don't need (or want) the
         * underlying model identifiers.
         */}
        {isSiteAdmin ? (
          <section className="rounded-2xl border border-ink-200 bg-white p-6">
            <h2 className="text-lg font-semibold text-ink-900">{t('settings.model_section')}</h2>
            <p className="mt-1 text-xs text-ink-500">{t('settings.model_admin_note')}</p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-xs text-ink-500 mb-1">{t('settings.model_general')}</span>
                <select
                  value={modelDefault}
                  onChange={(e) => setModelDefault(e.target.value)}
                  className="num w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
                >
                  {modelOptions.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs text-ink-500 mb-1">{t('settings.model_reasoning')}</span>
                <select
                  value={reasoningModel}
                  onChange={(e) => setReasoningModel(e.target.value)}
                  className="num w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
                >
                  {modelOptions.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </label>
            </div>
            <button
              onClick={saveModels}
              disabled={update.isPending || !company}
              className="mt-4 px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-60"
            >
              {update.isPending ? t('settings.saving') : t('settings.save_models')}
            </button>
          </section>
        ) : (
          <section className="rounded-2xl border border-ink-200 bg-white p-6">
            <h2 className="text-lg font-semibold text-ink-900">{t('settings.ai_brand_section')}</h2>
            <p className="mt-1 text-sm text-ink-600">{t('settings.ai_brand_body')}</p>
            <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5">
              <span className="grid h-5 w-5 place-items-center rounded-sm bg-emerald-600 text-white text-[10px] font-bold">✦</span>
              <span className="text-[13px] font-semibold text-emerald-700">CadencEZ AI</span>
            </div>
          </section>
        )}

        <section className="rounded-2xl border border-ink-200 bg-white p-6">
          {billingBanner === 'success' && (
            <div role="status" className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-800 flex items-center justify-between gap-2">
              <span>{t('settings.billing_success', { defaultValue: 'Subscription updated. It can take a moment for the new plan to reflect — refresh if needed.' })}</span>
              <button type="button" onClick={() => setBillingBanner(null)} className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold">×</button>
            </div>
          )}
          {billingBanner === 'cancelled' && (
            <div role="status" className="mb-4 rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-[13px] text-ink-700 flex items-center justify-between gap-2">
              <span>{t('settings.billing_cancelled', { defaultValue: 'Checkout cancelled. No changes were made.' })}</span>
              <button type="button" onClick={() => setBillingBanner(null)} className="text-ink-500 hover:text-ink-900 text-xs font-semibold">×</button>
            </div>
          )}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-ink-900">{t('settings.plan_section', { defaultValue: 'Plan' })}</h2>
              <p className="text-sm text-ink-600 mt-1">
                {t('settings.plan_current_line', {
                  defaultValue: `You're on the ${(company?.plan ?? 'free').charAt(0).toUpperCase() + (company?.plan ?? 'free').slice(1)} plan.`,
                  plan: (company?.plan ?? 'free'),
                })}
              </p>
            </div>
            <Link
              to={`/app/workspace/${wsId}/subscription`}
              className="shrink-0 inline-flex items-center rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-white hover:bg-accent-600"
            >
              {(company?.plan ?? 'free') === 'free'
                ? t('settings.view_plans', { defaultValue: 'View plans' })
                : t('settings.manage_subscription', { defaultValue: 'Manage subscription' })}
            </Link>
          </div>
        </section>
      </main>
    </AppShell>
  );
}

/**
 * In-app plan picker. Mirrors the public pricing page: all tiers in one
 * view, monthly/yearly toggle, current-plan badge. "Subscribe" / "Switch"
 * call /api/billing/checkout with the chosen tier + interval; "Current
 * plan" is shown as a non-interactive badge for the active tier.
 *
 * Free is informational — there's nothing to check out for it. Paid tier
 * switches go through Stripe Checkout; the webhook then updates
 * companies.plan on completion so this UI reflects the new state.
 */
export function PlanPicker({
  currentPlan,
  companyId,
  error,
  setError,
  currentInterval = null,
  subscriptionEndsAt = null,
}: {
  currentPlan: 'free' | 'growth' | 'enterprise';
  companyId: string;
  error: string | null;
  setError: (s: string | null) => void;
  currentInterval?: 'monthly' | 'yearly' | null;
  subscriptionEndsAt?: string | null;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const subscribed = currentPlan !== 'free';
  const [interval, setInterval] = useState<'monthly' | 'yearly'>(currentInterval ?? 'monthly');
  const [pending, setPending] = useState<'growth' | 'enterprise' | null>(null);
  // D3: when already subscribed, switching the billing cycle updates the live
  // subscription in place (not a new checkout). cycleTo holds the pending switch.
  const [cycleTo, setCycleTo] = useState<'monthly' | 'yearly' | null>(null);
  const [cycleBusy, setCycleBusy] = useState(false);

  async function doChangeCycle(to: 'monthly' | 'yearly') {
    if (!companyId) return;
    setError(null);
    setCycleBusy(true);
    try {
      await billingApi.changeCycle(companyId, to);
      setInterval(to);
      await qc.invalidateQueries({ queryKey: ['companies'] });
      setCycleTo(null);
    } catch (err) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (err as Error).message);
    } finally {
      setCycleBusy(false);
    }
  }

  // Live Stripe-sourced plan catalogue. Single source of truth — never
  // hardcode the cent amounts here or a Stripe Dashboard price edit
  // silently desyncs the picker from what Checkout will actually charge.
  const plansQ = useQuery({ queryKey: ['billing-plans'], queryFn: () => billingApi.plans() });
  const plans = plansQ.data ?? [];
  const priceFor = (code: 'free' | 'growth' | 'enterprise'): number => {
    const p = plans.find((x) => x.code === code);
    if (!p) return 0;
    const cents = interval === 'monthly' ? p.monthlyUnitAmountCents : p.yearlyUnitAmountCents;
    // Yearly cents represent the full annual price; the card shows an
    // "effective monthly" rate, so divide by 12. Stripe's yearly prices
    // are set to monthly × 10 (= 2 months free), so this works out to
    // an ~17% discount vs the monthly card.
    return interval === 'yearly' ? Math.round(cents / 12) / 100 : cents / 100;
  };
  const currency = (plans[0]?.currency ?? 'usd').toUpperCase();

  const tiers: Array<{
    code: 'free' | 'growth' | 'enterprise';
    name: string;
    featuresKey: string;
    highlight: boolean;
  }> = [
    { code: 'free', name: t('pricing.free'), featuresKey: 'pricing.free_features', highlight: false },
    { code: 'growth', name: t('pricing.growth'), featuresKey: 'pricing.growth_features', highlight: true },
    { code: 'enterprise', name: t('pricing.enterprise'), featuresKey: 'pricing.enterprise_features', highlight: false },
  ];

  // Portal availability — gates the Manage subscription button. The
  // probe falls back to `false` so a 503/missing endpoint hides the
  // button silently instead of breaking the picker UI.
  const billingMe = useQuery<{ hasStripeCustomer: boolean }>({
    queryKey: ['billing-me', companyId],
    enabled: !!companyId,
    queryFn: async () => billingApi.me(companyId),
  });
  const canManage = billingMe.data?.hasStripeCustomer === true;

  async function subscribe(plan: 'growth' | 'enterprise') {
    if (!companyId) return;
    setError(null);
    setPending(plan);
    try {
      const { url } = await billingApi.checkout({ plan, interval, seats: 1, companyId });
      window.location.href = url;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 503 ? t('settings.upgrade_unavailable') : (err as Error).message);
      setPending(null);
    }
  }

  async function openPortal() {
    if (!companyId) return;
    setError(null);
    try {
      const { url } = await billingApi.portal(companyId, `${window.location.origin}/app/settings`);
      window.location.href = url;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 503 ? t('settings.portal_unavailable', { defaultValue: 'Subscribe first to manage your billing.' }) : (err as Error).message);
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink-900">{t('settings.plan_section')}</h2>
          <p className="text-sm text-ink-600 mt-1">{t('settings.plan_intro')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canManage && (
            <button
              type="button"
              onClick={openPortal}
              className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100 font-semibold"
            >
              {t('settings.manage_subscription', { defaultValue: 'Manage subscription' })}
            </button>
          )}
        <div className="inline-flex rounded-lg border border-ink-200 p-0.5 bg-ink-50 text-xs font-semibold">
          {(['monthly', 'yearly'] as const).map((iv) => {
            const isCurrentCycle = subscribed && currentInterval === iv;
            return (
              <button
                key={iv}
                type="button"
                disabled={isCurrentCycle || cycleBusy}
                onClick={() => {
                  // Already subscribed → switching cycle updates the live subscription in place.
                  if (subscribed && currentInterval && iv !== currentInterval) { setCycleTo(iv); return; }
                  setInterval(iv);
                }}
                className={
                  'px-3 py-1.5 rounded-md transition ' +
                  (interval === iv ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900') +
                  (isCurrentCycle ? ' cursor-default' : '')
                }
              >
                {iv === 'monthly' ? t('pricing.billing_monthly') : `${t('pricing.billing_annual')} · −17%`}
              </button>
            );
          })}
        </div>
        </div>
      </div>

      {subscribed && subscriptionEndsAt && (
        <p className="mt-2 text-[12px] text-ink-500">
          {t('settings.subscription_status', {
            defaultValue: 'Current: {{interval}} billing · renews {{date}}',
            interval: currentInterval === 'yearly' ? t('pricing.billing_annual') : t('pricing.billing_monthly'),
            date: new Date(subscriptionEndsAt).toLocaleDateString(),
          })}
        </p>
      )}

      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        {tiers.map((tier) => {
          const isCurrent = tier.code === currentPlan;
          const price = priceFor(tier.code);
          return (
            <div
              key={tier.code}
              className={
                'rounded-xl border p-4 flex flex-col ' +
                (isCurrent
                  ? 'border-accent-500 bg-accent-50/40 ring-1 ring-accent-500/30'
                  : tier.highlight
                    ? 'border-ink-300 bg-white'
                    : 'border-ink-200 bg-white')
              }
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-ink-900">{tier.name}</h3>
                {isCurrent && (
                  <span className="text-[10px] uppercase tracking-wide font-bold text-accent-700">
                    {t('settings.current_plan', { defaultValue: 'Current' })}
                  </span>
                )}
              </div>
              <p className="mt-2 num text-2xl font-bold text-ink-900">
                {plansQ.isLoading ? '—' : new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(price)}
              </p>
              <p className="text-xs text-ink-500">
                {tier.code === 'free'
                  ? t('pricing.forever_free')
                  : interval === 'monthly'
                    ? t('pricing.per_seat_month')
                    : t('pricing.per_seat_month_annual')}
              </p>
              <div className="mt-3 flex-1">
                <PlanFeatureList featuresKey={tier.featuresKey} t={t} />
              </div>
              {tier.code !== 'free' && (
                <button
                  type="button"
                  disabled={isCurrent || pending !== null}
                  onClick={() => subscribe(tier.code as 'growth' | 'enterprise')}
                  className={
                    'mt-4 w-full rounded-md px-3 py-2 text-xs font-semibold transition ' +
                    (isCurrent
                      ? 'bg-ink-100 text-ink-500 cursor-default'
                      : 'bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-60')
                  }
                >
                  {isCurrent
                    ? t('settings.current_plan', { defaultValue: 'Current plan' })
                    : pending === tier.code
                      ? t('settings.opening_stripe', { defaultValue: 'Opening…' })
                      : currentPlan === 'free'
                        ? t('settings.subscribe', { defaultValue: 'Subscribe' })
                        : t('settings.switch_plan', { defaultValue: 'Switch' })}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <p
          role="alert"
          className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800"
        >
          {error}
        </p>
      )}

      <div className="mt-4 text-[11px] text-ink-500">
        <Link to="/pricing" className="underline-offset-2 hover:underline">
          {t('settings.see_plans')}
        </Link>
      </div>

      <ConfirmModal
        open={cycleTo !== null}
        title={t('settings.switch_cycle_title', { defaultValue: 'Switch billing cycle?' })}
        body={cycleTo
          ? t('settings.switch_cycle_body', {
              defaultValue: `Switch to ${cycleTo === 'yearly' ? 'annual' : 'monthly'} billing? Your subscription updates immediately with prorated charges.`,
              interval: cycleTo === 'yearly' ? t('pricing.billing_annual') : t('pricing.billing_monthly'),
            })
          : ''}
        confirmLabel={t('common.confirm')}
        onConfirm={() => { if (cycleTo) void doChangeCycle(cycleTo); }}
        onCancel={() => setCycleTo(null)}
      />
    </>
  );
}

/** Collapsible plan feature list — full features from i18n, first 6 visible. */
function PlanFeatureList({ featuresKey, t }: { featuresKey: string; t: TFn }) {
  const [open, setOpen] = useState(false);
  const raw = t(featuresKey, { returnObjects: true }) as unknown;
  const features: string[] = Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : [];
  if (features.length === 0) return null;
  const PREVIEW = 6;
  const shown = open ? features : features.slice(0, PREVIEW);
  return (
    <div>
      <ul className="space-y-1 text-xs text-ink-600">
        {shown.map((f) => (
          <li key={f} className="flex gap-1.5">
            <span className="text-accent-500 font-bold">✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {features.length > PREVIEW && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-1.5 text-[11px] font-semibold text-accent-600 hover:text-accent-700"
        >
          {open
            ? t('pricing.show_less_features', { defaultValue: 'Show less' })
            : t('pricing.show_all_features', { defaultValue: 'Show all {{count}} features', count: features.length })}
        </button>
      )}
    </div>
  );
}

function Loading({ t }: { t: (k: string) => string }) {
  return <div className="min-h-screen flex items-center justify-center text-ink-500 text-sm">{t('common.loading')}</div>;
}

type TFn = (k: string, opts?: Record<string, unknown>) => string;

/** Company profile editor — name, country, reporting currency, base unit,
 * locale, FY start month. Server gates writes to owners/admins. */
function CompanyProfileSection({ company, t }: { company: Company; t: TFn }) {
  const qc = useQueryClient();
  const [name, setName] = useState(company.name);
  const [country, setCountry] = useState(company.countryCode);
  const [currency, setCurrency] = useState(company.reportingCurrency);
  const [baseUnit, setBaseUnit] = useState<Company['baseUnit']>(company.baseUnit);
  const [locale, setLocale] = useState(company.primaryLocale);
  const [fyMonth, setFyMonth] = useState(String(company.fiscalYearStartMonth));
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const save = useMutation({
    mutationFn: () => companiesApi.updateSettings(company.id, {
      name: name.trim(), countryCode: country, reportingCurrency: currency,
      baseUnit, primaryLocale: locale, fiscalYearStartMonth: Number(fyMonth),
    }),
    onSuccess: () => { setSavedAt(Date.now()); qc.invalidateQueries({ queryKey: ['companies'] }); },
  });
  const errMsg = save.error ? ((save.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (save.error as Error).message) : null;

  const verifyStyle: Record<Company['verificationStatus'], string> = {
    verified: 'pill-emerald', pending: 'bg-amber-50 text-amber-700', pending_review: 'bg-amber-50 text-amber-700', rejected: 'bg-warn-500/10 text-warn-600',
  };
  const input = 'w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm';

  return (
    <section className="rounded-2xl border border-ink-200 bg-white p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink-900">{t('settings.company_section', { defaultValue: 'Company profile' })}</h2>
          <p className="text-sm text-ink-600 mt-1">{t('settings.company_intro', { defaultValue: 'How CadencEZ labels and reports for your organization.' })}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold rounded px-1.5 py-0.5 ${verifyStyle[company.verificationStatus]}`}>
          {company.verificationStatus.replace('_', ' ')}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block"><span className="block text-xs font-semibold text-ink-700 mb-1">{t('settings.company_name', { defaultValue: 'Company name' })}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={input} /></label>
        <label className="block"><span className="block text-xs font-semibold text-ink-700 mb-1">{t('account.country_label')}</span>
          <CountryPicker value={country} onChange={setCountry} /></label>
        <label className="block"><span className="block text-xs font-semibold text-ink-700 mb-1">{t('consolidation.reporting_currency')}</span>
          <CurrencyPicker value={currency} onChange={setCurrency} className={input} /></label>
        <label className="block"><span className="block text-xs font-semibold text-ink-700 mb-1">{t('settings.base_unit', { defaultValue: 'Base unit' })}</span>
          <select value={baseUnit} onChange={(e) => setBaseUnit(e.target.value as Company['baseUnit'])} className={input}>
            <option value="1">1</option><option value="1000">1,000</option><option value="1000000">1,000,000</option>
          </select></label>
        <label className="block"><span className="block text-xs font-semibold text-ink-700 mb-1">{t('common.language')}</span>
          <select value={locale} onChange={(e) => setLocale(e.target.value)} className={input}>
            {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.nativeName} ({l.englishName})</option>)}
          </select></label>
        <label className="block"><span className="block text-xs font-semibold text-ink-700 mb-1">{t('settings.fy_start_month', { defaultValue: 'Fiscal year start month' })}</span>
          <select value={fyMonth} onChange={(e) => setFyMonth(e.target.value)} className={input}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}</option>)}
          </select></label>
      </div>
      <div className="mt-5 flex items-center gap-3">
        <button onClick={() => save.mutate()} disabled={save.isPending} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-60">
          {save.isPending ? t('settings.saving') : t('common.save')}
        </button>
        {savedAt && !save.isPending && !save.isError && <span className="text-xs text-emerald-600">{t('common.saved')}</span>}
        {errMsg && <span className="text-xs text-warn-700">{errMsg}</span>}
      </div>
    </section>
  );
}

/** Team roster — list members, change roles, remove (owner/admin only; the
 * server enforces it and surfaces an error otherwise). */
function MembersSection({ wsId, t }: { wsId: string; t: TFn }) {
  const qc = useQueryClient();
  const { data: members = [], isLoading } = useQuery({
    queryKey: ['members', wsId],
    queryFn: () => membersApi.list(wsId),
    enabled: !!wsId,
  });
  const setRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: AssignableRole }) => membersApi.setRole(wsId, userId, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members', wsId] }),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => membersApi.remove(wsId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members', wsId] }),
  });
  const [removeId, setRemoveId] = useState<string | null>(null);
  const err = (setRole.error || remove.error) as { response?: { data?: { message?: string } } } | null;
  const errMsg = err ? (err.response?.data?.message ?? (err as unknown as Error).message) : null;

  return (
    <section className="rounded-2xl border border-ink-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-ink-900">{t('settings.members_section', { defaultValue: 'Team members' })}</h2>
      <p className="text-sm text-ink-600 mt-1">{t('settings.members_intro', { defaultValue: 'People with access to this workspace. Owners and admins can manage roles.' })}</p>
      {isLoading ? (
        <p className="mt-4 text-sm text-ink-500">{t('common.loading')}</p>
      ) : (
        <ul className="mt-4 divide-y divide-ink-100">
          {members.map((m: WorkspaceMember) => (
            <li key={m.id} className="flex items-center gap-3 py-2.5">
              {m.pictureUrl
                ? <img src={m.pictureUrl} alt="" className="h-8 w-8 rounded object-cover shrink-0" />
                : <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-foreground text-background text-xs font-semibold shrink-0">{memberLabel(m).charAt(0).toUpperCase()}</span>}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-ink-900 truncate">{memberLabel(m)}</div>
                {m.name && m.email && <div className="text-xs text-muted-foreground truncate">{m.email}</div>}
              </div>
              {m.role === 'owner' ? (
                <span className="text-[10px] uppercase tracking-[0.16em] font-semibold pill-emerald rounded px-1.5 py-0.5">owner</span>
              ) : (
                <>
                  <select
                    value={m.role}
                    onChange={(e) => setRole.mutate({ userId: m.id, role: e.target.value as AssignableRole })}
                    disabled={setRole.isPending}
                    className="rounded-md border border-ink-200 bg-white px-2 py-1 text-xs"
                  >
                    <option value="admin">admin</option><option value="editor">editor</option><option value="viewer">viewer</option>
                  </select>
                  <button
                    onClick={() => setRemoveId(m.id)}
                    disabled={remove.isPending}
                    className="text-[11px] text-ink-500 hover:text-warn-600 disabled:opacity-50"
                  >
                    {t('common.delete')}
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {errMsg && <p className="mt-2 text-xs text-warn-700">{errMsg}</p>}
      <ConfirmModal
        open={removeId !== null}
        title={t('settings.remove_member_confirm', { defaultValue: 'Remove this member?' })}
        confirmLabel={t('common.delete')}
        destructive
        onConfirm={() => { if (removeId) remove.mutate(removeId); setRemoveId(null); }}
        onCancel={() => setRemoveId(null)}
      />
    </section>
  );
}
